import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionPanel,
} from 'primeng/accordion';
import {
  APPLICATION_CATEGORY,
  ApplicationMainPageInitialData,
} from '../../shared/models/custom-types';
import { Button } from 'primeng/button';
import { getSwiadczeniobiorcyFilterDropdownItems } from '../../shared/enums/application-list-swiadczeniobiorcy.enum';
import {
  getPrzedsiebiorcyFilterDropdownItems,
  PrzedsiebiorcyFilter,
} from '../../shared/enums/application-list-przedsiebiorcy.enum';
import { Application } from '../../shared/models/application';
import { GetApplicationCategoryNamePipe } from './pipe/get-application-category-name.pipe';
import { ApplicationContextService } from '../../core/services/application-context.service';
import { ORGANIZATION_CODES, ORGANIZATION_EXCLUSIVE_CODES } from '../../shared/enums/application_code';
import { DokumentApi } from '../../shared/services/api/dokument.api';
import { ApplicationAccordionCard } from './components/application-accordion-card/application-accordion-card';
import { EmailVerifyInfo } from '../../shared/components/email-verify-info/email-verify-info';
import { Beneficjent } from '../../shared/models/beneficjent';
import { ApplicationService } from '../../shared/services/application.service';
import { LastUsedApplicationService } from '../../core/services/last-used-application.service';
import { NgTemplateOutlet } from '@angular/common';
import { InfoMessageComponent } from '../../shared/components/info-message/info-message.component';
import { AnnouncementService } from '../../shared/services/announcement.service';

@Component({
  selector: 'app-application-cards',
  imports: [
    AccordionHeader,
    AccordionContent,
    Accordion,
    AccordionPanel,
    Button,
    GetApplicationCategoryNamePipe,
    ApplicationAccordionCard,
    EmailVerifyInfo,
    NgTemplateOutlet,
    InfoMessageComponent,
  ],
  templateUrl: './application-cards.component.html',
  styleUrl: './application-cards.component.scss',
  providers: [GetApplicationCategoryNamePipe, DokumentApi],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApplicationCardsComponent implements OnInit {
  protected applications = input<ApplicationMainPageInitialData>();
  protected beneficiary = input.required<Beneficjent>();

  @ViewChild('filteredApplicationsEl')
  filteredApplicationsEl!: ElementRef<HTMLDivElement>;

  protected readonly applicationContextService = inject(
    ApplicationContextService,
  );
  readonly #applicationService = inject(ApplicationService);
  readonly #announcementService = inject(AnnouncementService);
  protected readonly lastUsedApplicationService = inject(
    LastUsedApplicationService,
  );

  readonly #organizationCodes = new Set(
    ORGANIZATION_CODES,
  );
  readonly #organizationExclusiveCodes = new Set(
    ORGANIZATION_EXCLUSIVE_CODES,
  );

  protected readonly SWIADCZENIOBIORCY_FILTERS =
    getSwiadczeniobiorcyFilterDropdownItems();
  protected readonly PRZEDSIEBIORCY_FILTERS =
    getPrzedsiebiorcyFilterDropdownItems();

  protected filteredApplications: Application[] = [];
  protected filter = signal<APPLICATION_CATEGORY | null>(null);
  protected lastUsedApplicationsAccordionValue = signal<string>('');

  protected orgBabyApplications = computed(() =>
    this.#getOrganizationApplications(
      PrzedsiebiorcyFilter.OPIEKA_NAD_DZIECKIEM_DO_LAT_3,
    ),
  );

  protected orgTurnusyApplications = computed(() =>
    this.#getOrganizationApplications(
      PrzedsiebiorcyFilter.TURNUSY_REHABILITACYJNE,
    ),
  );

  protected manualInfoMessageContent: string = `
    <span>
      Aktualne instrukcje wypełniania wniosków RKZ są dostępne pod tym linkiem:
    </span>
    <a href="https://www.gov.pl/web/rodzina/instrukcje-wypelniania-wnioskow-na-piu-empatia" target="_blank" style="color: #0052a5;">
      <b>https://www.gov.pl/web/rodzina/instrukcje-wypelniania-wnioskow-na-piu-empatia</b>
    </a>`;

  constructor() {
    effect(() => {
      this.#filterApplications();
      this.#toggleLastUsedApplicationAccordion();
    });
  }

  ngOnInit(): void {
    this.#announcementService.checkAndShowAnnouncements();
  }

  protected toggleFilter(value: APPLICATION_CATEGORY): void {
    this.filter.set(this.filter() === value ? null : value);
    setTimeout(() => {
      const firstChild = this.filteredApplicationsEl.nativeElement
        .firstElementChild as HTMLElement | null;

      firstChild?.focus();
    }, 0);
  }

  protected initApplicationFlow(application: Application): void {
    this.#applicationService
      .initApplicationFlowWithUnit(application)
      .subscribe();
  }

  #filterApplications(): void {
    const category = this.filter() as APPLICATION_CATEGORY | undefined;

    const applications = category
      ? (this.applications()?.applications[category] ?? [])
      : [];

    this.filteredApplications = this.#filterByContext(applications);
  }

  #filterByContext(applications: Application[]): Application[] {
    const isOrganizationContext =
      this.applicationContextService.isOrganizationContext();

    return applications.filter((app) => {
      if (!app.kod) {
        return false;
      }

      const isOrganizationExclusive = this.#organizationExclusiveCodes.has(app.kod);

      return isOrganizationContext
        ? isOrganizationExclusive
        : !isOrganizationExclusive;
    });
  }

  #getOrganizationApplications(category: PrzedsiebiorcyFilter): Application[] {
    return (
      this.applications()?.applications[category] ?? []
    ).filter(
      (application) =>
        application.kod &&
        this.#organizationCodes.has(application.kod),
    );
  }

  #toggleLastUsedApplicationAccordion(): void {
    if (this.filter()) {
      this.lastUsedApplicationsAccordionValue.set('');
    } else {
      this.lastUsedApplicationsAccordionValue.set('0');
    }
  }
}
